import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { getErrors, resolveError, resolveAllErrors, clearErrors, getErrorCount } from '../services/errorLogger';
import { Button } from './ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';
import { AlertCircle, AlertTriangle, Info, Check, Trash2, CheckCheck } from 'lucide-react';
import { format, parseISO } from 'date-fns';

export default function ErrorLogPanel() {
  const { t } = useLanguage();
  const [errors, setErrors] = useState([]);
  const [showResolved, setShowResolved] = useState(false);

  useEffect(() => {
    // Load errors on mount and set up interval for updates
    const loadErrors = () => setErrors(getErrors(showResolved));
    loadErrors();
    
    const interval = setInterval(loadErrors, 2000);
    return () => clearInterval(interval);
  }, [showResolved]);

  const handleResolve = (id) => {
    resolveError(id);
    setErrors(getErrors(showResolved));
  };

  const handleResolveAll = () => {
    resolveAllErrors();
    setErrors(getErrors(showResolved));
  };

  const handleClear = () => {
    clearErrors();
    setErrors([]);
  };

  const unresolvedCount = errors.filter(e => !e.resolved).length;

  const getIcon = (type) => {
    switch (type) {
      case 'error': return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'warning': return <AlertTriangle className="h-4 w-4 text-amber-500" />;
      default: return <Info className="h-4 w-4 text-blue-500" />;
    }
  };

  const getBgColor = (type, resolved) => {
    if (resolved) return 'bg-muted/30 opacity-60';
    switch (type) {
      case 'error': return 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-900';
      case 'warning': return 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900';
      default: return 'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900';
    }
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className={`relative ${unresolvedCount > 0 ? 'text-red-500' : ''}`}
          data-testid="error-log-btn"
        >
          <AlertCircle className="h-5 w-5" />
          {unresolvedCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
              {unresolvedCount > 9 ? '9+' : unresolvedCount}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[400px] sm:w-[540px]">
        <SheetHeader>
          <SheetTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              {t('errorLog') || 'Journal des erreurs'}
              {unresolvedCount > 0 && (
                <span className="text-sm bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                  {unresolvedCount}
                </span>
              )}
            </span>
          </SheetTitle>
        </SheetHeader>
        
        <div className="mt-4 space-y-4">
          {/* Actions */}
          <div className="flex items-center gap-2 flex-wrap">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowResolved(!showResolved)}
            >
              {showResolved ? (t('hideResolved') || 'Masquer résolus') : (t('showResolved') || 'Afficher résolus')}
            </Button>
            {unresolvedCount > 0 && (
              <Button variant="outline" size="sm" onClick={handleResolveAll}>
                <CheckCheck className="h-4 w-4 mr-1" />
                {t('resolveAll') || 'Tout résoudre'}
              </Button>
            )}
            {errors.length > 0 && (
              <Button variant="outline" size="sm" onClick={handleClear} className="text-red-500 hover:text-red-700">
                <Trash2 className="h-4 w-4 mr-1" />
                {t('clearAll') || 'Effacer tout'}
              </Button>
            )}
          </div>

          {/* Error List */}
          <div className="space-y-2 max-h-[calc(100vh-200px)] overflow-y-auto">
            {errors.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <AlertCircle className="h-12 w-12 mx-auto mb-2 opacity-20" />
                <p>{t('noErrors') || 'Aucune erreur'}</p>
              </div>
            ) : (
              errors.map(error => (
                <div 
                  key={error.id} 
                  className={`p-3 rounded-lg border ${getBgColor(error.type, error.resolved)}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2 flex-1">
                      {getIcon(error.type)}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${error.resolved ? 'line-through opacity-60' : ''}`}>
                          {error.message}
                        </p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                          <span>{format(parseISO(error.timestamp), 'dd/MM HH:mm:ss')}</span>
                          <span className="px-1.5 py-0.5 bg-muted rounded">{error.source}</span>
                        </div>
                        {error.details && (
                          <p className="text-xs text-muted-foreground mt-1 truncate">
                            {JSON.stringify(error.details)}
                          </p>
                        )}
                      </div>
                    </div>
                    {!error.resolved && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleResolve(error.id)}
                        className="h-6 w-6 p-0"
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
